import { useState, useEffect, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import Button from '@/components/common/Button'
import Card from '@/components/common/Card'
import Loading from '@/components/common/Loading'

export default function SyncMonitor() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [jobStats, setJobStats] = useState([])
  const [recentJobs, setRecentJobs] = useState([])
  const [expandedJob, setExpandedJob] = useState(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      // Load job stats summary
      const { data: stats, error: statsError } = await supabase
        .rpc('get_sync_job_stats', { p_days: 7 })

      if (statsError) {
        console.error('Error loading job stats:', statsError)
      } else {
        setJobStats(stats || [])
      }

      // Load recent job history
      const { data: jobs, error: jobsError } = await supabase
        .from('sync_job_logs')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(50)

      if (jobsError) {
        console.error('Error loading job history:', jobsError)
      } else {
        setRecentJobs(jobs || [])
      }
    } catch (err) {
      console.error('Error loading sync data:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatDuration = (seconds) => {
    if (!seconds) return '-'
    if (seconds < 60) return `${seconds}s`
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}m ${secs}s`
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed' },
      running: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Running' },
      partial: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Partial' },
      failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Cancelled' }
    }
    const config = statusConfig[status] || statusConfig.cancelled
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    )
  }

  const getJobTypeLabel = (type) => {
    const types = {
      calendar_sync: 'Calendar Sync',
      match_generation: 'Match Generation'
    }
    return types[type] || type
  }

  const getSuccessRate = (successful, failed, total) => {
    if (!total || total === 0) return 0
    return Math.round((successful / total) * 100)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-gray-900"
              >
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Sync Monitor
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Monitor calendar sync and match generation jobs
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={loadData}>
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {loading ? (
          <Card>
            <div className="p-12 text-center">
              <Loading text="Loading sync data..." />
            </div>
          </Card>
        ) : (
          <>
            {/* Stats Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {jobStats.length === 0 ? (
                <Card className="md:col-span-2">
                  <div className="p-6 text-center text-gray-500">
                    No sync jobs recorded in the last 7 days
                  </div>
                </Card>
              ) : (
                jobStats.map((stat) => (
                  <Card key={stat.job_type}>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {getJobTypeLabel(stat.job_type)}
                        </h3>
                        {getStatusBadge(stat.last_run_status)}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-gray-500">Total Runs (7d)</p>
                          <p className="text-2xl font-bold text-gray-900">{stat.total_runs}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Success Rate</p>
                          <p className="text-2xl font-bold text-gray-900">
                            {stat.total_runs > 0 
                              ? Math.round((stat.successful_runs / stat.total_runs) * 100) 
                              : 0}%
                          </p>
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div className="mb-4">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>{stat.successful_runs} successful</span>
                          <span>{stat.failed_runs} failed</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-green-500"
                            style={{ width: `${stat.total_runs > 0 ? (stat.successful_runs / stat.total_runs) * 100 : 0}%` }}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Avg Duration</p>
                          <p className="font-medium">{formatDuration(stat.avg_duration_seconds)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Last Run</p>
                          <p className="font-medium">{formatDate(stat.last_run_at)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Items Processed</p>
                          <p className="font-medium">{stat.total_items_processed || 0}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Total Errors</p>
                          <p className={`font-medium ${stat.total_errors > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                            {stat.total_errors || 0}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>

            {/* Recent Jobs Table */}
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Recent Jobs
                </h3>
                
                {recentJobs.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No jobs recorded yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Type</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Started</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Duration</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Success/Failed/Skipped</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Matches</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600">Errors</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentJobs.map((job) => (
                          <Fragment key={job.id}>
                            <tr 
                              className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                              onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                            >
                              <td className="py-3 px-4">
                                <span className="font-medium">{getJobTypeLabel(job.job_type)}</span>
                                {job.job_trigger && job.job_trigger !== 'cron' && (
                                  <span className="ml-2 text-xs text-gray-400">({job.job_trigger})</span>
                                )}
                              </td>
                              <td className="py-3 px-4">{getStatusBadge(job.status)}</td>
                              <td className="py-3 px-4 text-gray-600">{formatDate(job.started_at)}</td>
                              <td className="py-3 px-4 text-gray-600">{formatDuration(job.duration_seconds)}</td>
                              <td className="py-3 px-4">
                                <span className="text-green-600">{job.successful_items || 0}</span>
                                {' / '}
                                <span className="text-red-600">{job.failed_items || 0}</span>
                                {' / '}
                                <span className="text-gray-400">{job.skipped_items || 0}</span>
                              </td>
                              <td className="py-3 px-4 text-gray-600">
                                {job.job_type === 'match_generation' ? (
                                  <>
                                    <span className="text-green-600">+{job.matches_created || 0}</span>
                                    {job.matches_expired > 0 && (
                                      <span className="text-gray-400 ml-1">(-{job.matches_expired})</span>
                                    )}
                                  </>
                                ) : '-'}
                              </td>
                              <td className="py-3 px-4">
                                {job.errors && Array.isArray(job.errors) && job.errors.length > 0 ? (
                                  <span className="text-red-600 font-medium">
                                    {job.errors.length} error{job.errors.length !== 1 ? 's' : ''}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                            </tr>
                            
                            {/* Expanded error details */}
                            {expandedJob === job.id && job.errors && Array.isArray(job.errors) && job.errors.length > 0 && (
                              <tr>
                                <td colSpan={7} className="bg-red-50 px-4 py-3">
                                  <div className="text-sm">
                                    <p className="font-medium text-red-800 mb-2">Error Details:</p>
                                    <div className="space-y-1 max-h-48 overflow-y-auto">
                                      {job.errors.map((err, idx) => (
                                        <div key={idx} className="text-red-700 bg-white rounded px-2 py-1 text-xs">
                                          <span className="font-medium">{err.team || err.item_id || 'Unknown'}:</span>{' '}
                                          {err.error || err.message || 'Unknown error'}
                                          {err.timestamp && (
                                            <span className="text-red-400 ml-2">
                                              at {new Date(err.timestamp).toLocaleTimeString()}
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Card>

            {/* Info Card */}
            <Card className="bg-blue-50 border-blue-200">
              <div className="p-6">
                <h3 className="font-semibold text-blue-900 mb-2">About Sync Jobs</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li><strong>Calendar Sync</strong> - Runs nightly at 4 AM CT. Scrapes team calendars, detects solo ice opportunities, creates pending postings.</li>
                  <li><strong>Match Generation</strong> - Runs after calendar sync. Computes matches between teams by level, calculates combined scores.</li>
                  <li><strong>Status meanings:</strong> Completed = 100% success, Partial = &lt;10% errors, Failed = &gt;10% errors</li>
                </ul>
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  )
}
